import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { canInstall, requestInstall, onInstallableChange } from "@/lib/installPrompt";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/301a1c64-0b19-462c-acf8-1e89c9bc05f1/images/cba60e89c5f18aec88b7b5e4bb560a3a3cd16137942d429c9dc4648d7082b353.png";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// Re-nudge to install after a cooldown instead of dismissing forever.
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 1 day
function recentlyDismissed() {
  const t = localStorage.getItem("airpog_install_dismissed_at");
  return t && Date.now() - Number(t) < DISMISS_COOLDOWN_MS;
}

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    if (canInstall()) setShow(true);
    const off = onInstallableChange((installable) => { if (installable) setShow(true); });
    // iOS Safari never fires beforeinstallprompt — show manual hint after a short delay.
    let t;
    if (isIOS()) t = setTimeout(() => { setIosHelp(true); setShow(true); }, 3500);
    return () => { off(); if (t) clearTimeout(t); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem("airpog_install_dismissed_at", String(Date.now())); };

  const install = async () => {
    await requestInstall();
    setShow(false);
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[3000] w-[min(92vw,420px)]">
      <div className="glass-strong rounded-2xl p-3.5 flex items-center gap-3 shadow-2xl border border-slate-700/60">
        <img src={LOGO} alt="AIR.POG" className="w-11 h-11 rounded-xl object-contain shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-sm leading-tight text-white">Install AIR.POG</p>
          <p className="text-xs text-slate-400 leading-snug">
            {iosHelp ? "Tap the Share icon, then “Add to Home Screen”." : "Add to your home screen for full-screen, app-like driving."}
          </p>
        </div>
        {!iosHelp && (
          <button data-testid="install-accept-btn" onClick={install} className="shrink-0 h-9 px-3 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 text-white font-heading font-bold text-xs flex items-center gap-1.5 hover:opacity-90">
            <Download className="w-4 h-4" /> Install
          </button>
        )}
        <button data-testid="install-dismiss-btn" onClick={dismiss} className="shrink-0 w-8 h-8 rounded-lg hover:bg-slate-700/60 flex items-center justify-center">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  );
}
